<template>
  <div
    class="room-booking-page"
    :class="`room-booking-page--form-${bookingFormDisplaySize}`"
  >
    <Header />

    <div class="rb-shell" :class="{'rb-shell--picker-open': showRoomPicker}">
      <header class="rb-page-head">
        <div class="rb-page-head__titles">
          <button
            v-if="isAddingRoom"
            type="button"
            class="rb-back"
            @click="returnToReservation"
          >
            Back to reservation
          </button>
          <h1 class="rb-page-head__title">
            {{ isAddingRoom ? 'Add room' : 'New reservation' }}
          </h1>
          <p class="rb-page-head__subtitle">
            {{
              isAddingRoom
                ? 'Choose a room. You return to the same reservation.'
                : 'Complete guest details and room configuration'
            }}
          </p>
        </div>
        <div class="rb-page-head__stats">
          <div class="rb-stat">
            <span class="rb-stat__label">Rooms</span>
            <span class="rb-stat__value">{{ ordersList.length }}</span>
          </div>
          <div class="rb-stat rb-stat--accent">
            <span class="rb-stat__label">Total</span>
            <span class="rb-stat__value">{{ formatMoney(totalAmount) }}</span>
          </div>
        </div>
      </header>

      <div class="rb-layout">
        <!-- Guest & reservation -->
        <section class="rb-panel rb-panel--guest">
          <div class="rb-panel__head">
            <h2 class="rb-panel__title">Guest information</h2>
          </div>
          <div class="rb-panel__body">
            <div class="rb-guest-toolbar">
              <div class="personal-card__customer-bar">
                <div class="personal-card__search">
                  <i class="icon_search" aria-hidden="true"></i>
                  <input
                    v-model="customerSearchQuery"
                    type="text"
                    class="form-control personal-card__search-input"
                    placeholder="Search name, phone, NIC, passport"
                    autocomplete="off"
                    @input="onCustomerSearchInput"
                  />
                </div>
                <span
                  v-if="loadedGuestId"
                  class="personal-card__badge personal-card__badge--found"
                >
                  Customer loaded
                </span>
                <span
                  v-else-if="savingGuest"
                  class="personal-card__badge personal-card__badge--saving"
                >
                  Saving…
                </span>
                <span
                  v-else-if="Booking.guest.phone"
                  class="personal-card__badge personal-card__badge--new"
                >
                  New customer
                </span>
              </div>
              <ul
                v-if="customerSearchResults.length"
                class="personal-card__suggestions"
              >
                <li
                  v-for="g in customerSearchResults"
                  :key="g.id"
                  class="personal-card__suggestion"
                  @click="selectGuest(g)"
                >
                  <strong>{{ g.name }}</strong>
                  <span v-if="g.phone"> · {{ g.phone }}</span>
                </li>
              </ul>
            </div>

            <div class="rb-guest-main">
              <div class="rb-avatar">
                <input
                  ref="inputRef"
                  type="file"
                  id="imageUpload"
                  class="rb-avatar__input"
                  accept=".png, .jpg, .jpeg"
                  @change="fileSelected"
                />
                <button
                  type="button"
                  class="rb-avatar__upload"
                  aria-label="Upload photo"
                  @click="triggerInput"
                >
                  <i class="icon_cloud-upload" aria-hidden="true"></i>
                </button>
                <img
                  v-if="coverImagePreview.length"
                  :src="coverImagePreview[0]"
                  alt="Guest"
                  class="rb-avatar__img"
                />
                <img
                  v-else
                  src="@/assets/img/profile-img.png"
                  alt=""
                  class="rb-avatar__img"
                />
              </div>

              <div class="rb-field-grid guest-fields-grid">
                <div class="rb-field rb-field--span2">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.name"
                      placeholder="Full name *"
                      @input="onNameInput"
                      :class="{
                        'error-border':
                          $v.Booking.guest.name.$error || fieldErrors.name,
                        'success-border':
                          !$v.Booking.guest.name.$invalid &&
                          $v.Booking.guest.name.$dirty &&
                          !fieldErrors.name,
                      }"
                      @blur="onNameBlur"
                    />
                    <i
                      class="icon_pencil rb-field__icon"
                      aria-hidden="true"
                    ></i>
                    <div class="error-messages">
                      <div
                        class="error"
                        v-if="
                          $v.Booking.guest.name.$dirty &&
                            !$v.Booking.guest.name.required
                        "
                      >
                        Name is required
                      </div>
                      <div
                        class="error"
                        v-if="
                          $v.Booking.guest.name.$dirty &&
                            !$v.Booking.guest.name.minLength
                        "
                      >
                        Name must be at least 2 characters
                      </div>
                      <div class="error" v-if="fieldErrors.name">
                        {{ fieldErrors.name }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="rb-field">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.phone"
                      placeholder="Phone *"
                      min="0"
                      type="tel"
                      oninput="validity.valid||(value='');"
                      :class="{
                        'error-border':
                          $v.Booking.guest.phone.$error || fieldErrors.phone,
                        'success-border':
                          !$v.Booking.guest.phone.$invalid &&
                          $v.Booking.guest.phone.$dirty &&
                          !fieldErrors.phone,
                      }"
                      @input="onPhoneInput"
                      @blur="onPhoneBlur"
                    />
                    <i class="icon_phone rb-field__icon" aria-hidden="true"></i>
                    <div class="error-messages">
                      <div
                        class="error"
                        v-if="
                          $v.Booking.guest.phone.$dirty &&
                            !$v.Booking.guest.phone.required
                        "
                      >
                        Phone number is required
                      </div>
                      <div class="error" v-if="fieldErrors.phone">
                        {{ fieldErrors.phone }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="rb-field">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.email"
                      placeholder="Email"
                      type="email"
                      @input="markGuestDirty"
                    />
                    <i class="icon_mail rb-field__icon" aria-hidden="true"></i>
                  </div>
                </div>

                <div class="rb-field">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.nic_number"
                      placeholder="National ID"
                      @input="markGuestDirty"
                    />
                    <i
                      class="icon_id_alt rb-field__icon"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>

                <div class="rb-field">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.passport_number"
                      placeholder="Passport"
                      @input="markGuestDirty"
                    />
                    <i
                      class="icon_id-2_alt rb-field__icon"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>

                <div class="rb-field">
                  <div
                    class="form-group form-group--compact rb-field--datepicker"
                  >
                    <DatePicker
                      v-model="Booking.guest.date_of_birth"
                      placeholder="Date of birth"
                      :validate="false"
                      @input="onDobChange"
                      :max-date="maxDobDate"
                      min-date="1920-01-01"
                      format="DD/MM/YYYY"
                    />
                  </div>
                </div>

                <div class="rb-field">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.company"
                      placeholder="Company"
                      @input="markGuestDirty"
                    />
                    <i
                      class="icon_toolbox_alt rb-field__icon"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>

                <div class="rb-field rb-field--span2">
                  <div class="form-group form-group--compact">
                    <input
                      class="form-control"
                      v-model="Booking.guest.address"
                      placeholder="Address"
                      @input="markGuestDirty"
                    />
                    <i class="icon_house rb-field__icon" aria-hidden="true"></i>
                  </div>
                </div>
              </div>
            </div>

            <div class="rb-section rb-section--meta">
              <h3 class="rb-section__title">Stay details</h3>

              <div class="rb-payment-toggle">
                <label class="rb-payment-toggle__label">
                  <input
                    type="checkbox"
                    class="rb-payment-toggle__input"
                    v-model="PaymentDetails"
                  />
                  <span
                    class="rb-payment-toggle__switch"
                    aria-hidden="true"
                  ></span>
                  <span class="rb-payment-toggle__text"
                    >Record payment now</span
                  >
                </label>
              </div>

              <div class="rb-meta-grid">
                <div class="rb-field rb-field--span2">
                  <label class="rb-field__label">Booking status</label>
                  <Multiselect
                    v-model="selectedBookingStatus"
                    :options="bookingStatuses"
                    :loading="loadingBookingStatuses"
                    track-by="id"
                    label="name"
                    placeholder="Select booking status"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      {{ option.name }}
                    </template>
                  </Multiselect>
                </div>
                <div class="rb-field rb-field--span2">
                  <input
                    class="form-control"
                    placeholder="Reservation note"
                    v-model="Booking.note"
                  />
                </div>
                <div class="rb-field">
                  <input
                    class="form-control"
                    placeholder="Vehicle no."
                    v-model="Booking.vehicle_number"
                  />
                </div>
              </div>

              <div
                v-show="PaymentDetails"
                class="rb-meta-grid rb-meta-grid--payment"
              >
                <div class="rb-field">
                  <Multiselect
                    v-model="Payment.payment_type"
                    selectLabel=""
                    :options="PaymentTypes"
                    placeholder="Payment type"
                    track-by="name"
                    label="name"
                  >
                    <template slot="singleLabel" slot-scope="{option}">
                      {{ option.name }}
                    </template>
                  </Multiselect>
                </div>
                <div class="rb-field">
                  <input
                    class="form-control"
                    placeholder="Amount"
                    v-model="Payment.amount"
                    min="0"
                    type="number"
                    oninput="validity.valid||(value='');"
                  />
                  <label v-if="Payment.amount < 0" class="error-text">
                    Amount must be greater than 0
                  </label>
                </div>
                <div
                  v-if="
                    Payment.payment_type && Payment.payment_type.name === 'Card'
                  "
                  class="rb-field"
                >
                  <input
                    class="form-control"
                    placeholder="Bank"
                    v-model="Payment.card_bank"
                  />
                </div>
                <div class="rb-field rb-field--span2">
                  <input
                    class="form-control"
                    placeholder="Payment note"
                    v-model="Payment.remarks"
                  />
                </div>
              </div>

              <div class="rb-section__actions">
                <p v-if="!guestDetailsValid" class="rb-section__hint">
                  Enter guest name and phone to confirm this reservation.
                </p>
                <p v-else-if="!ordersList.length" class="rb-section__hint">
                  Add at least one room to confirm this reservation.
                </p>
                <div class="rb-section__buttons">
                  <button
                    type="button"
                    class="rb-btn rb-btn--outline"
                    @click="openRoomPicker"
                  >
                    {{ showRoomPicker ? 'Hide rooms' : 'Add rooms' }}
                  </button>
                  <button
                    v-if="!isAddingRoom"
                    type="button"
                    class="rb-btn rb-btn--primary"
                    :disabled="!canConfirmReservation"
                    @click="BookRoom()"
                  >
                    Confirm reservation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Rooms -->
        <section class="rb-panel rb-panel--rooms">
          <div class="rb-panel__head">
            <h2 class="rb-panel__title">Rooms</h2>
            <span class="rb-panel__hint">{{ ordersList.length }} selected</span>
          </div>
          <div class="rb-panel__body rb-room-list">
            <p v-if="!ordersList.length" class="rb-empty">
              No rooms yet. Use <strong>Add rooms</strong> in Stay details or
              the bar at the bottom.
            </p>

            <article
              v-for="(item, index) in ordersList"
              :key="item.id || index"
              class="rb-room-card"
            >
              <div class="rb-room-card__head">
                <h3 class="rb-room-card__name">{{ item.name }}</h3>
                <button
                  type="button"
                  class="rb-room-card__remove"
                  aria-label="Remove room"
                  @click="removeRoom(index)"
                >
                  <i class="icon_close" aria-hidden="true"></i>
                </button>
              </div>

              <div class="rb-room-card__grid">
                <div
                  class="rb-field rb-field--span2 room-type-box"
                  :class="{
                    'room-type-box--error': getRoomError(index, 'room_type_id'),
                  }"
                >
                  <label class="rb-label">Room type</label>
                  <Multiselect
                    :value="getSelectedRoomType(item)"
                    :options="item.types"
                    track-by="room_type.id"
                    :custom-label="opt => opt.room_type.name"
                    placeholder="Select type"
                    @input="val => handleRoomTypeSelect(val, item, index)"
                  >
                    <template slot="singleLabel" slot-scope="{option}">
                      {{ option.room_type.name }}
                    </template>
                  </Multiselect>
                  <div class="error" v-if="getRoomError(index, 'room_type_id')">
                    {{ getRoomError(index, 'room_type_id') }}
                  </div>
                </div>

                <div class="rb-field">
                  <label class="rb-label">Check-in</label>
                  <DatePicker
                    v-model="item.datefilter.start"
                    :min-date="minDate"
                    :validate="false"
                    format="YYYY-MM-DD"
                    placeholder="Check-in"
                    :input-class="
                      getRoomError(index, 'check_in') ? 'error-border' : ''
                    "
                    @input="onRoomDateChange(item, index)"
                  />
                  <div class="error" v-if="getRoomError(index, 'check_in')">
                    {{ getRoomError(index, 'check_in') }}
                  </div>
                </div>

                <div class="rb-field">
                  <label class="rb-label">Check-out</label>
                  <DatePicker
                    v-model="item.datefilter.end"
                    :min-date="item.datefilter.start || minDate"
                    :validate="false"
                    format="YYYY-MM-DD"
                    placeholder="Check-out"
                    :input-class="
                      getRoomError(index, 'check_out') ? 'error-border' : ''
                    "
                    @input="onRoomDateChange(item, index)"
                  />
                  <div class="error" v-if="getRoomError(index, 'check_out')">
                    {{ getRoomError(index, 'check_out') }}
                  </div>
                </div>

                <div class="rb-field">
                  <label class="rb-label">Adults</label>
                  <input
                    class="form-control"
                    v-model="item.adults"
                    placeholder="Adults"
                    min="0"
                    type="number"
                    oninput="validity.valid||(value='');"
                    @input="RefreshAdultsCount($event, item, index)"
                  />
                  <label v-if="item.adults > item.max_adult" class="error-text">
                    Max {{ item.max_adult }} adults
                  </label>
                </div>

                <div class="rb-field">
                  <label class="rb-label">Children</label>
                  <input
                    class="form-control"
                    v-model="item.child"
                    min="0"
                    type="number"
                    placeholder="Children"
                    oninput="validity.valid||(value='');"
                    @input="RefreshChildCount($event, item)"
                  />
                  <label v-if="item.child > item.max_child" class="error-text">
                    Max {{ item.max_child }} children
                  </label>
                </div>

                <div class="rb-field rb-field--span2">
                  <label class="rb-label">Services</label>
                  <Multiselect
                    :disabled="loadingAmentidy"
                    v-model="item.amenities"
                    :options="amenities[index]"
                    :multiple="true"
                    @input="setAmendities($event, index)"
                    placeholder="Amenities"
                    track-by="name"
                    label="name"
                  />
                </div>

                <div class="rb-field">
                  <label class="rb-label">Rate / night</label>
                  <input
                    class="form-control"
                    v-model="item.amount"
                    placeholder="Amount"
                    min="0"
                    type="number"
                    oninput="validity.valid||(value='');"
                    :class="{'error-border': getRoomError(index, 'amount')}"
                    @input="setTotalAmount($event, item, index)"
                  />
                  <div class="error" v-if="getRoomError(index, 'amount')">
                    {{ getRoomError(index, 'amount') }}
                  </div>
                  <span v-else-if="taxIncluded" class="rb-hint">
                    Tax is included in the room rate.
                  </span>
                  <span v-else-if="taxPercent > 0" class="rb-hint">
                    Includes {{ taxPercent }}% tax, same as the website.
                  </span>
                </div>

                <div
                  v-if="item.room_type_id"
                  class="rb-field rb-room-card__total"
                >
                  <span class="rb-label">Subtotal</span>
                  <strong class="rb-room-card__charge">
                    {{ item.duration || 1 }} night(s) ·
                    {{ formatMoney(item.totalAmount) }}
                  </strong>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>

      <RoomAvailabilityPicker
        v-if="showRoomPicker"
        class="rb-room-picker"
        @room-added="onRoomAddedFromPicker"
        @close="closeRoomPicker"
      />

      <footer
        class="rb-action-bar"
        :class="{'rb-action-bar--picker': showRoomPicker}"
      >
        <div class="rb-action-bar__info">
          <template v-if="showRoomPicker">
            <span class="rb-action-bar__note">
              Tap <strong>Book Now</strong> on a room, then
              <strong>Done</strong> to continue.
            </span>
            <span class="rb-action-bar__stat">
              {{ ordersList.length }} room{{
                ordersList.length === 1 ? '' : 's'
              }}
              ·
              {{ formatMoney(totalAmount) }}
            </span>
          </template>
          <template v-else>
            <span v-if="totalAmount < 0" class="error-text">
              Enter valid room amounts
            </span>
            <span v-else-if="Payment.amount < 0" class="error-text">
              Payment amount must be positive
            </span>
            <span v-else class="rb-action-bar__stat">
              {{ ordersList.length }} room{{
                ordersList.length === 1 ? '' : 's'
              }}
              ·
              {{ formatMoney(totalAmount) }}
            </span>
            <span v-if="!canConfirmReservation" class="rb-action-bar__note">
              {{
                !guestDetailsValid
                  ? 'Enter guest name and phone to confirm'
                  : !ordersList.length
                    ? 'Add rooms in Stay details, then confirm there'
                    : 'Set room type and rate in the Rooms column'
              }}
            </span>
          </template>
        </div>
        <div class="rb-action-bar__buttons">
          <button
            v-if="showRoomPicker"
            type="button"
            class="rb-btn rb-btn--primary"
            @click="isAddingRoom ? returnToReservation() : closeRoomPicker()"
          >
            {{ isAddingRoom ? 'Back to reservation' : 'Done selecting rooms' }}
          </button>
          <button
            v-else
            type="button"
            class="rb-btn rb-btn--outline"
            @click="openRoomPicker"
          >
            Add rooms
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import {Header} from '@/components/index.js';
import RoomAvailabilityPicker from '@/components/Share/RoomAvailabilityPicker/index.vue';
import {mapState} from 'vuex';
import Multiselect from 'vue-multiselect';
import axios from '@/plugins/axios';
import moment from 'moment';
import {normalizePickerDate, toApiDate} from '@/utils/datehelper';
import {DatePicker} from '@/components/base/index';
import {required, minLength} from 'vuelidate/lib/validators';

export default {
  components: {Header, Multiselect, DatePicker, RoomAvailabilityPicker},

  data() {
    return {
      showRoomPicker: false,
      ordersList: [],
      amenities: [], // one array per room
      loadingAmentidy: false,
      coverImagePreview: [],
      coverImg: null,
      ChangePhoto: false,
      PaymentDetails: false,

      Payment: {
        payment_type: {name: 'Cash'},
        amount: null,
        card_bank: '',
        remarks: '',
      },

      PaymentTypes: [{name: 'Cash'}, {name: 'Card'}],

      defaultBookingStatusId: null,
      bookingStatuses: [],
      selectedBookingStatus: null,
      loadingBookingStatuses: false,
      taxPercent: 12,
      taxIncluded: false,
      fieldErrors: {
        name: '',
        phone: '',
      },
      roomFieldErrors: {},

      customerSearchQuery: '',
      customerSearchResults: [],
      customerSearchTimer: null,
      guestAutoSaveTimer: null,
      loadedGuestId: null,
      guestDirty: false,
      savingGuest: false,

      Booking: {
        note: '',
        vehicle_number: '',
        guest: {
          name: '',
          phone: '',
          email: '',
          nic_number: '',
          passport_number: '',
          address: '',
          date_of_birth: null,
          company: '',
        },
        rooms: [],
      },
    };
  },

  validations: {
    Booking: {
      guest: {
        name: {required, minLength: minLength(2)},
        phone: {required},
      },
    },
  },

  computed: {
    ...mapState('orders', ['orders']),
    ...mapState('sitesetting', ['setting']),

    isAddingRoom() {
      return (
        this.$route.query.addRooms === '1' && !!this.reservationReturnPath
      );
    },
    reservationReturnPath() {
      const queryPath = this.$route.query.returnTo;
      if (
        typeof queryPath === 'string' &&
        queryPath.indexOf('/reservation-details/') === 0
      ) {
        return queryPath;
      }
      return null;
    },
    bookingFormDisplaySize() {
      const size =
        this.setting && this.setting.booking_form_display_size
          ? this.setting.booking_form_display_size
          : 'browser';
      return size === 'pos' ? 'pos' : 'browser';
    },

    guestDetailsValid() {
      const name = (this.Booking.guest.name || '').trim();
      const phone = (this.Booking.guest.phone || '').trim();
      return name.length >= 2 && phone.length > 0;
    },

    canAutoSaveGuest() {
      return (
        this.guestDirty &&
        this.guestDetailsValid &&
        !this.savingGuest
      );
    },

    totalAmount() {
      return this.ordersList.reduce((sum, room) => {
        return sum + (parseFloat(room.totalAmount) || 0);
      }, 0);
    },
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
    maxDobDate() {
      return moment().format('YYYY-MM-DD');
    },
    canConfirmReservation() {
      return (
        this.ordersList.length > 0 &&
        this.totalAmount > 0 &&
        this.guestDetailsValid
      );
    },
  },

  watch: {
    ordersList: {
      deep: true,
      handler() {
        // Force update total when anything changes
        this.$forceUpdate();
      },
    },
  },

  mounted() {
    this.initializeOrders();
    this.loadRoomTax();
    this.loadBookingStatuses();
    this.$store.dispatch('sitesetting/GetSettings').catch(() => {});
    if (this.reservationReturnPath) {
      this.showRoomPicker = true;
    } else {
      this.$store.commit('orders/updateRoomVisible', false);
      this.$store.commit('orders/AddNewRoomReservationId', null);
      if (this.$route.query.addRooms === '1') {
        this.showRoomPicker = true;
      }
    }
  },

  beforeDestroy() {
    if (this.guestAutoSaveTimer) clearTimeout(this.guestAutoSaveTimer);
    if (this.customerSearchTimer) clearTimeout(this.customerSearchTimer);
  },

  methods: {
    returnToReservation() {
      const path = this.reservationReturnPath;
      this.$store.commit('orders/updateRoomVisible', false);
      if (path) this.$router.push(path);
    },
    loadRoomTax() {
      axios
        .get('hms/web/site-pages')
        .then(res => {
          const rooms = (res.data && res.data.rooms) || {};
          this.taxPercent =
            rooms.taxPercent == null
              ? 12
              : Math.min(100, Math.max(0, Number(rooms.taxPercent) || 0));
          this.taxIncluded = Boolean(rooms.taxIncluded);
          this.ordersList.forEach(item => {
            if (
              item.selectedRoomType &&
              Number(item.amount) === Number(item.selectedRoomType.amount)
            ) {
              this.$set(item, 'amount', this.chargeForRate(item.selectedRoomType.amount));
              this.calculateRoomTotal(item);
            }
          });
        })
        .catch(() => {});
    },
    chargeForRate(rate) {
      const base = parseFloat(rate) || 0;
      if (this.taxIncluded || !(this.taxPercent > 0)) return base;
      return base + Math.round((base * this.taxPercent) / 100);
    },
    formatMoney(value) {
      const amount = parseFloat(value) || 0;
      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    openRoomPicker() {
      this.showRoomPicker = !this.showRoomPicker;
      if (this.showRoomPicker) {
        this.syncFilterDatesFromBooking();
        this.$nextTick(() => {
          const el = this.$el.querySelector('.rb-room-picker');
          if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
        });
      }
    },
    closeRoomPicker() {
      this.showRoomPicker = false;
    },
    syncFilterDatesFromBooking() {
      const first = this.ordersList[0];
      if (!first || !first.datefilter) return;
      this.$store.commit('orders/AddFilterDates', {
        start: first.datefilter.start,
        end: first.datefilter.end,
        room_type: null,
      });
    },
    onRoomAddedFromPicker() {
      this.initializeOrders();
      this.$fire({
        title: 'Room added',
        text: 'Room added to this reservation.',
        type: 'success',
        timer: 2000,
      });
    },
    // Main fix: Properly initialize reactive orders
    initializeOrders() {
      // Filter out invalid orders (missing ID) that might have been added due to previous bugs
      const validOrders = this.orders.filter(order => order && order.id);

      // If we found invalid orders, clean up the store immediately
      if (validOrders.length !== this.orders.length) {
        this.$store.commit('orders/updateOrder', validOrders);
      }

      this.ordersList = validOrders.map(order => {
        const start = moment(order.datefilter.start);
        const end = moment(order.datefilter.end);
        const days = end.diff(start, 'days') || 1;

        return this.$setReactive({
          ...order,
          datefilter: {...order.datefilter},
          types: order.types || [],
          selectedRoomType: null, // critical
          room_type_id: null,
          amount: 0,
          totalAmount: 0,
          duration: days,
          adults: 2,
          child: 0,
          max_adult: 2,
          max_child: 2,
          amenities: [],
        });
      });

      // Initialize amenities arrays
      this.amenities = this.ordersList.map(() => []);
    },

    // Helper to make object fully reactive in Vue 2
    $setReactive(obj) {
      return JSON.parse(JSON.stringify(obj)); // simple deep clone + reactive
    },

    // Room Type Selection – FIXED
    handleRoomTypeSelect(roomTypeOption, item, index) {
      if (index !== undefined) {
        this.clearRoomError(index, 'room_type_id');
      }

      if (!roomTypeOption) {
        item.selectedRoomType = null;
        item.room_type_id = null;
        item.amount = 0;
        item.totalAmount = 0;
        return;
      }

      // Vue.set for full reactivity
      this.$set(item, 'selectedRoomType', roomTypeOption);
      this.$set(item, 'room_type_id', roomTypeOption.room_type.id);
      this.$set(item, 'amount', this.chargeForRate(roomTypeOption.amount));
      this.$set(item, 'max_adult', roomTypeOption.room_type.max_adult || 2);
      this.$set(item, 'max_child', roomTypeOption.room_type.max_child || 2);
      this.$set(item, 'adults', roomTypeOption.room_type.base_adult || 2);
      this.$set(item, 'child', roomTypeOption.room_type.base_child || 0);

      this.calculateRoomTotal(item);
      this.GetAmenities(roomTypeOption, item);
    },

    getSelectedRoomType(item) {
      return item.selectedRoomType;
    },

    calculateRoomTotal(item) {
      const start = moment(item.datefilter.start);
      const end = moment(item.datefilter.end);
      const days = end.diff(start, 'days') || 1;

      item.duration = days;
      const total = days * parseFloat(item.amount || 0);
      this.$set(item, 'totalAmount', total);
    },

    onRoomDateChange(item, index) {
      if (index !== undefined) {
        this.clearRoomError(index, 'check_in');
        this.clearRoomError(index, 'check_out');
      }

      // Keep end date valid (>= start) and recompute duration/total
      const startParsed = normalizePickerDate(item?.datefilter?.start);
      if (!startParsed) return;
      const start = moment(startParsed);
      const endParsed = normalizePickerDate(item?.datefilter?.end);
      const end = endParsed ? moment(endParsed) : moment.invalid();

      if (!end.isValid() || end.isSameOrBefore(start, 'day')) {
        const nextDay = start
          .clone()
          .add(1, 'day')
          .format('YYYY-MM-DD');
        this.$set(item.datefilter, 'end', nextDay);
      }

      this.calculateRoomTotal(item);
    },

    setTotalAmount(e, item, index) {
      if (index !== undefined) {
        this.clearRoomError(index, 'amount');
      }
      const value = e.target.value;
      this.$set(item, 'amount', value);
      this.calculateRoomTotal(item);
    },

    RefreshAdultsCount(e, item, index) {
      if (index !== undefined) {
        this.clearRoomError(index, 'adults');
      }
      this.$set(item, 'adults', e.target.value);
    },

    RefreshChildCount(e, item) {
      this.$set(item, 'child', e.target.value);
    },

    // Fixed: Use item reference, not index
    GetAmenities(roomTypeOption, item) {
      if (!roomTypeOption?.room_type?.id) return;

      const index = this.ordersList.indexOf(item);
      if (index === -1) return;

      this.loadingAmentidy = true;

      axios
        .get(`hms/web/room-types/${roomTypeOption.room_type.id}`)
        .then(res => {
          const currentIndex = this.ordersList.indexOf(item);
          if (currentIndex === -1) return;

          const amenitiesList = res.data.amenities || [];
          this.$set(this.amenities, currentIndex, amenitiesList);
          this.$set(this.ordersList[currentIndex], 'amenities', []);
        })
        .finally(() => {
          this.loadingAmentidy = false;
        });
    },

    removeRoom(index) {
      this.ordersList.splice(index, 1);
      this.amenities.splice(index, 1);
      this.$store.commit('orders/removeorder', index);
    },

    triggerInput() {
      this.$refs.inputRef.click();
    },

    fileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 10000000) {
        alert('Image too large');
        return;
      }
      if (!file.type.match('image.*')) {
        alert('Please upload an image');
        return;
      }

      this.coverImg = file;
      this.ChangePhoto = true;
      this.markGuestDirty();

      const reader = new FileReader();
      reader.onload = ev => {
        this.coverImagePreview = [ev.target.result];
      };
      reader.readAsDataURL(file);
    },

    markGuestDirty() {
      this.guestDirty = true;
      this.scheduleGuestAutoSave();
    },

    scheduleGuestAutoSave() {
      if (this.guestAutoSaveTimer) clearTimeout(this.guestAutoSaveTimer);
      if (!this.canAutoSaveGuest) return;
      this.guestAutoSaveTimer = setTimeout(() => {
        this.saveGuestToAdmin({silent: true});
      }, 800);
    },

    onNameInput() {
      this.fieldErrors.name = '';
      this.markGuestDirty();
    },

    onNameBlur() {
      this.$v.Booking.guest.name.$touch();
      this.scheduleGuestAutoSave();
    },

    onPhoneInput() {
      this.fieldErrors.phone = '';
      this.markGuestDirty();
    },

    onDobChange() {
      this.markGuestDirty();
    },

    onPhoneBlur() {
      this.$v.Booking.guest.phone.$touch();
      if (!this.loadedGuestId) {
        this.GetExistingCustomer(this.Booking.guest.phone);
      }
      this.scheduleGuestAutoSave();
    },

    onCustomerSearchInput() {
      if (this.customerSearchTimer) clearTimeout(this.customerSearchTimer);
      const q = (this.customerSearchQuery || '').trim();
      if (q.length < 2) {
        this.customerSearchResults = [];
        return;
      }
      this.customerSearchTimer = setTimeout(() => this.searchGuests(q), 350);
    },

    searchGuests(query) {
      axios
        .get(
          `hms/web/guests/search?isSearch=true&query=${encodeURIComponent(
            query,
          )}`,
        )
        .then(res => {
          this.customerSearchResults = Array.isArray(res.data?.data)
            ? res.data.data.slice(0, 8)
            : [];
        })
        .catch(() => {
          this.customerSearchResults = [];
        });
    },

    selectGuest(guest) {
      this.applyGuest(guest);
      this.customerSearchQuery = guest.name || guest.phone || '';
      this.customerSearchResults = [];
      this.guestDirty = false;
    },

    applyGuest(guest) {
      this.Booking.guest = {
        id: guest.id,
        name: guest.name || '',
        phone: guest.phone || '',
        email: guest.email || '',
        nic_number: guest.nic_number || '',
        passport_number: guest.passport_number || '',
        address: guest.address || '',
        date_of_birth: normalizePickerDate(guest.date_of_birth),
        company: guest.company || '',
      };
      this.loadedGuestId = guest.id || null;
      if (guest.image) this.coverImagePreview = [guest.image];
    },

    GetExistingCustomer(phone) {
      if (this.loadedGuestId) return;
      if (!phone || String(phone).trim().length < 3) return;
      axios
        .get(
          `hms/web/guests/search?isSearch=true&query=${encodeURIComponent(
            phone,
          )}`,
        )
        .then(res => {
          if (res.data.data.length > 0) {
            this.applyGuest(res.data.data[0]);
            this.guestDirty = false;
          } else if (!this.Booking.guest.id) {
            this.loadedGuestId = null;
          }
        });
    },

    ensureGuestIdOnBooking() {
      const id = this.Booking.guest.id || this.loadedGuestId;
      if (id && !this.Booking.guest.id) {
        this.$set(this.Booking.guest, 'id', id);
      }
      return id;
    },

    buildGuestPayload() {
      const email = (this.Booking.guest.email || '').trim();
      return {
        name: (this.Booking.guest.name || '').trim(),
        phone: (this.Booking.guest.phone || '').trim(),
        email: email || null,
        nic_number: (this.Booking.guest.nic_number || '').trim() || null,
        passport_number:
          (this.Booking.guest.passport_number || '').trim() || null,
        address: (this.Booking.guest.address || '').trim() || null,
        date_of_birth: (() => {
          const dob = normalizePickerDate(this.Booking.guest.date_of_birth);
          return dob ? moment(dob).format('YYYY-MM-DD') : null;
        })(),
        company: (this.Booking.guest.company || '').trim() || null,
      };
    },

    extractApiErrorMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (!data) return (err && err.message) || fallback;
      if (typeof data === 'string') return data || fallback;
      if (data.message) return data.message;
      if (data.error && typeof data.error === 'object') {
        const first = Object.values(data.error).find(v => v);
        if (first) return String(first);
      }
      return fallback;
    },

    async patchGuestRecord(guestId, payload) {
      const urls = [`hms/web/guests/${guestId}`, `hms/guests/${guestId}`];
      let lastError = null;
      for (const url of urls) {
        try {
          return await axios.patch(url, payload);
        } catch (err) {
          lastError = err;
          const status = err.response && err.response.status;
          if (status !== 404 && status !== 405) throw err;
        }
      }
      throw lastError;
    },

    async saveGuestToAdmin(options = {}) {
      const silent = options.silent === true;
      if (!this.guestDetailsValid) return false;

      this.savingGuest = true;
      const payload = this.buildGuestPayload();

      try {
        const guestId = this.ensureGuestIdOnBooking();
        if (guestId) {
          await this.patchGuestRecord(guestId, payload);
          this.loadedGuestId = guestId;
        } else {
          const res = await axios.post('hms/guests', payload);
          const created = res.data;
          this.$set(this.Booking.guest, 'id', created.id);
          this.loadedGuestId = created.id;
        }

        if (this.ChangePhoto && this.coverImg && this.Booking.guest.id) {
          const form = new FormData();
          form.append('image', this.coverImg);
          await axios.post(
            `hms/guests/${this.Booking.guest.id}/image-upload`,
            form,
          );
        }

        this.guestDirty = false;
        if (!silent) {
          this.$fire({
            title: 'Saved',
            text: 'Customer saved to hotel and admin records.',
            type: 'success',
          });
        }
        return true;
      } catch (err) {
        const msg = this.extractApiErrorMessage(
          err,
          'Could not save customer. Restart the API server if you recently updated the backend.',
        );
        if (!silent) {
          this.$fire({title: 'Error', text: msg, type: 'error'});
        }
        return false;
      } finally {
        this.savingGuest = false;
      }
    },

    parseBookingStatusList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },

    async loadBookingStatuses() {
      this.loadingBookingStatuses = true;
      try {
        const res = await axios
          .get('hms/web/booking-statuses')
          .catch(() => axios.get('booking-statuses/search?status=active'));
        this.bookingStatuses = this.parseBookingStatusList(res.data);
        const reservation =
          this.bookingStatuses.find(status => status.name === 'Reservation')
          || this.bookingStatuses.find(
            status => status.name === 'Preliminary reservation',
          )
          || this.bookingStatuses[0]
          || null;
        this.selectedBookingStatus = reservation;
        this.defaultBookingStatusId = reservation ? reservation.id : null;
      } catch (e) {
        this.bookingStatuses = [];
        this.selectedBookingStatus = null;
        this.defaultBookingStatusId = null;
      } finally {
        this.loadingBookingStatuses = false;
      }
    },

    loadDefaultBookingStatus() {
      return this.loadBookingStatuses();
    },

    getRoomError(index, field) {
      return (
        (this.roomFieldErrors[index] && this.roomFieldErrors[index][field]) ||
        ''
      );
    },

    clearRoomError(index, field) {
      if (this.roomFieldErrors[index] && this.roomFieldErrors[index][field]) {
        this.$delete(this.roomFieldErrors[index], field);
      }
    },

    validateRooms() {
      let valid = true;
      const errors = {};

      this.ordersList.forEach((room, index) => {
        const roomErrors = {};

        if (!room.selectedRoomType) {
          roomErrors.room_type_id = 'Room type is required';
          valid = false;
        }

        if (!room.datefilter || !room.datefilter.start) {
          roomErrors.check_in = 'Check-in date is required';
          valid = false;
        }

        if (!room.datefilter || !room.datefilter.end) {
          roomErrors.check_out = 'Check-out date is required';
          valid = false;
        }

        const amount = parseFloat(room.amount);
        if (!amount || amount <= 0) {
          roomErrors.amount = 'Amount must be greater than 0';
          valid = false;
        }

        if (Object.keys(roomErrors).length) {
          errors[index] = roomErrors;
        }
      });

      this.roomFieldErrors = errors;
      return valid;
    },

    applyServerErrors(apiError) {
      this.fieldErrors = {name: '', phone: ''};
      this.roomFieldErrors = {};

      Object.entries(apiError).forEach(([rawPath, message]) => {
        const path = String(rawPath).replace(/,/g, '.');

        if (path === 'name' || path === 'guest.name') {
          this.fieldErrors.name = message;
          this.$v.Booking.guest.name.$touch();
          return;
        }

        if (path === 'phone' || path === 'guest.phone') {
          this.fieldErrors.phone = message;
          this.$v.Booking.guest.phone.$touch();
          return;
        }

        const roomMatch = path.match(/^rooms\.(\d+)\.(.+)$/);
        if (roomMatch) {
          const index = parseInt(roomMatch[1], 10);
          const field = roomMatch[2];
          if (!this.roomFieldErrors[index]) {
            this.$set(this.roomFieldErrors, index, {});
          }
          this.$set(this.roomFieldErrors[index], field, message);
        }
      });
    },

    validateForm() {
      this.$v.Booking.guest.name.$touch();
      this.$v.Booking.guest.phone.$touch();
      const guestValid =
        !this.$v.Booking.guest.name.$invalid &&
        !this.$v.Booking.guest.phone.$invalid;
      const roomsValid = this.validateRooms();
      return guestValid && roomsValid;
    },

    async BookRoom() {
      if (!this.validateForm()) {
        return;
      }

      if (!this.selectedBookingStatus && !this.defaultBookingStatusId) {
        await this.loadBookingStatuses();
      }

      this.Booking.date = moment().format('YYYY-MM-DD');
      this.Booking.booking_status_id =
        (this.selectedBookingStatus && this.selectedBookingStatus.id)
        || this.defaultBookingStatusId;
      this.Booking.rooms = this.ordersList.map(room => ({
        room_id: room.id,
        room_type_id: room.selectedRoomType.room_type.id,
        number_of_room: 1,
        amount: parseFloat(room.amount) || 0,
        adults: parseInt(room.adults, 10) || 0,
        child: parseInt(room.child, 10) || 0,
        check_in: toApiDate(room.datefilter.start),
        check_out: toApiDate(room.datefilter.end),
        amenities: (room.amenities || []).map(a => a.id),
      }));

      if (!this.Booking.booking_status_id) {
        this.$fire({
          title: 'Error',
          text: 'Booking status is not configured. Please contact support.',
          type: 'error',
        });
        return;
      }

      try {
        if (this.guestDirty) {
          const saved = await this.saveGuestToAdmin({silent: true});
          if (!saved) {
            this.$fire({
              title: 'Error',
              text: 'Could not save guest details. Check name and phone.',
              type: 'error',
            });
            return;
          }
        }

        this.ensureGuestIdOnBooking();
        const res = await axios.post('hms/web/reservations', this.Booking);

        if (this.PaymentDetails && this.Payment.amount > 0) {
          await axios.post(`hms/web/reservations/${res.data.id}/payments`, {
            ...this.Payment,
            payment_type: this.Payment.payment_type.name,
            amount: parseFloat(this.Payment.amount),
            date_time: moment().format('YYYY-MM-DD HH:mm:ss'),
          });
        }

        if (this.ChangePhoto && this.coverImg) {
          const form = new FormData();
          form.append('image', this.coverImg);
          await axios.post(
            `hms/guests/${res.data.guest_id}/image-upload`,
            form,
          );
        }

        this.$store.commit('orders/updateOrder', []);
        this.$router.push(`/reservation-details/${res.data.id}`);

        this.$fire({
          title: 'Success',
          text: 'Room booked successfully!',
          type: 'success',
        });
      } catch (err) {
        let errorMessage = 'Booking failed';
        const data = err.response && err.response.data;
        const apiErrors = data && (data.error || data.errors);

        if (apiErrors && typeof apiErrors === 'object') {
          this.applyServerErrors(apiErrors);
          return;
        }

        if (data) {
          if (data.message) {
            errorMessage = data.message;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        this.$fire({title: 'Error', text: errorMessage, type: 'error'});
      }
    },
  },
};
</script>

<style scoped>
.rb-back {
  display: inline-flex;
  align-items: center;
  margin: 0 0 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--brand-primary, #512d82);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.room-booking-page {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  padding-top: var(--header-page-offset, 68px);
  background: #f1f5f9;
  text-align: left;
  box-sizing: border-box;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
  position: relative;
  z-index: 0;
}

.rb-shell {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-page-offset, 68px));
  max-height: calc(100vh - var(--header-page-offset, 68px));
  overflow: hidden;
  padding: 0 12px 76px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

.rb-page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 6px 10px;
  margin: 0 0 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  position: relative;
  z-index: 1;
  min-height: 0;
}

.rb-page-head__titles {
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 4px;
}

.rb-page-head__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.25;
}

.rb-page-head__subtitle {
  margin: 2px 0 0;
  font-size: 0.6875rem;
  color: #64748b;
  line-height: 1.35;
}

.rb-page-head__stats {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 12px;
}

.rb-stat {
  min-width: 64px;
  padding: 4px 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  text-align: center;
}

.rb-stat--accent {
  border-color: var(--brand-primary-border);
  background: var(--brand-primary-muted);
}

.rb-stat__label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.rb-stat__value {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--brand-primary-dark);
}

.rb-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.rb-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rb-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.rb-panel__title {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rb-panel__hint {
  font-size: 0.75rem;
  color: #64748b;
}

.rb-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px 16px;
  -webkit-overflow-scrolling: touch;
}

.rb-guest-toolbar {
  position: relative;
  z-index: 3;
  margin-bottom: 8px;
}

.rb-guest-main {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.rb-avatar {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  flex-shrink: 0;
}

.rb-avatar__input {
  display: none;
}

.rb-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rb-avatar__upload {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-primary);
}

.rb-field-grid,
.rb-meta-grid,
.rb-room-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  width: 100%;
}

.rb-field--span2 {
  grid-column: 1 / -1;
}

.rb-field--datepicker {
  min-height: 30px;
}

.rb-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 2px;
}

.rb-field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.rb-field__icon {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: #94a3b8;
  pointer-events: none;
}

.rb-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.rb-section--meta {
  margin-bottom: 0;
  padding-bottom: 4px;
}

.rb-section__title {
  margin: 0 0 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rb-section__actions {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.rb-section__hint {
  margin: 0 0 8px;
  font-size: 0.75rem;
  color: #64748b;
}

.rb-section__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rb-section__buttons .rb-btn {
  flex: 1 1 120px;
  min-width: 100px;
  height: 32px;
  font-size: 0.75rem;
}

.rb-payment-toggle {
  margin: 0 0 6px;
}

.rb-payment-toggle__label {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  cursor: pointer;
  user-select: none;
}

.rb-payment-toggle__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.rb-payment-toggle__switch {
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.2s ease;
}

.rb-payment-toggle__switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.rb-payment-toggle__input:checked + .rb-payment-toggle__switch {
  background: var(--brand-primary);
}

.rb-payment-toggle__input:checked + .rb-payment-toggle__switch::after {
  transform: translateX(16px);
}

.rb-payment-toggle__input:focus-visible + .rb-payment-toggle__switch {
  outline: 2px solid var(--brand-primary-fade-20);
  outline-offset: 2px;
}

.rb-payment-toggle__text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #334155;
}

.rb-meta-grid--payment {
  margin-top: 4px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
}

.rb-room-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rb-empty {
  margin: 0;
  padding: 20px 12px;
  text-align: center;
  font-size: 0.875rem;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
}

.rb-room-card {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  background: #fafbfc;
  border-top: 2px solid var(--brand-primary);
}

.rb-room-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.rb-room-card__name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--brand-primary-dark);
}

.rb-room-card__remove {
  width: 28px;
  height: 28px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fff;
  color: #b91c1c;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rb-room-card__total {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.rb-room-card__charge {
  font-size: 0.875rem;
  color: var(--brand-primary-dark);
}

.rb-action-bar {
  order: 2;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  padding-bottom: max(10px, env(safe-area-inset-bottom, 0px));
  margin: 0;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.08);
}

.rb-action-bar__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rb-action-bar__stat {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--brand-primary-dark);
}

.rb-action-bar__note {
  font-size: 0.75rem;
  color: #64748b;
}

.rb-action-bar__note strong {
  font-weight: 600;
  color: #334155;
}

.rb-action-bar--picker {
  border-top-color: var(--brand-primary-border);
  background: #f8fafc;
}

.rb-action-bar__buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.rb-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
}

.rb-btn--outline {
  background: #fff;
  color: var(--brand-primary-dark);
  border-color: var(--brand-primary-border);
}

.rb-btn--outline:hover {
  background: var(--brand-primary-muted);
}

.rb-btn--primary {
  background: var(--brand-primary);
  color: #fff;
  border-color: var(--brand-primary);
}

.rb-btn--primary:hover:not(:disabled) {
  background: var(--brand-primary-hover);
}

.rb-btn--disabled,
.rb-btn--primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.rb-room-picker {
  flex: 1 1 0;
  order: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 6px 0 0;
  -webkit-overflow-scrolling: touch;
}

.rb-shell--picker-open .rb-layout {
  flex: 0 1 40%;
  min-height: 0;
  max-height: 40%;
  overflow: hidden;
}

.rb-shell--picker-open .rb-panel__body {
  padding-bottom: 12px;
}

@media (max-width: 1100px) {
  .room-booking-page {
    height: auto;
    max-height: none;
    overflow: visible;
  }

  .rb-layout {
    grid-template-columns: 1fr;
  }

  .rb-shell {
    height: auto;
    max-height: none;
    overflow: visible;
    padding-bottom: 72px;
  }

  .rb-shell--picker-open .rb-layout {
    flex: none;
    max-height: none;
  }

  .rb-shell--picker-open .rb-room-picker {
    flex: none;
    max-height: none;
  }

  .rb-action-bar {
    position: fixed;
  }

  .rb-panel__body {
    max-height: none;
  }
}

@media (max-width: 900px) {
  .rb-page-head {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .rb-page-head__stats {
    width: 100%;
    padding-left: 4px;
    margin-left: 0;
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .rb-page-head {
    flex-direction: column;
  }

  .rb-page-head__stats {
    width: 100%;
  }

  .rb-guest-main {
    grid-template-columns: 1fr;
  }

  .rb-avatar {
    margin: 0 auto;
  }

  .rb-field-grid,
  .rb-meta-grid,
  .rb-room-card__grid {
    grid-template-columns: 1fr;
  }

  .rb-action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .rb-action-bar__buttons {
    flex-direction: column;
  }

  .rb-btn {
    width: 100%;
  }
}

/* Body & General */
body {
  background-color: #f7f8fa;
  font-family: 'Inter', sans-serif;
  color: #333;
  line-height: 1.5;
}

/* Headings */
h1,
h6 {
  font-weight: 600;
  color: #242c67;
  margin-bottom: 10px;
}
h1 small {
  font-weight: 400;
  color: #6c6c6c;
  display: block;
  font-size: 15px;
  margin-top: 5px;
}

/* Remove (X) Button */
.btn-circle {
  background: #ffeded !important;
  color: #d9534f !important;
  border: 1px solid #d9534f !important;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  transition: 0.2s;
}
.btn-circle:hover {
  background: #d9534f !important;
  color: #fff !important;
}

/* Cards */
.card,
.carddesign,
.summary-card,
.personal-card,
.box_booking_2 {
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 20px;
  margin-bottom: 20px;
}

/* Buttons */
.btn_1,
.imageEditBtn {
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Primary Button */
.btn_1 {
  background: var(--brand-primary);
  color: #fff;
  border-radius: 12px;
  padding: 10px;
}
.btn_1:hover {
  background: var(--brand-primary-hover);
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #6c757d !important;
}

.btn-disabled:hover {
  background-color: #6c757d !important;
}

/* Circle Buttons */
.btn-circle {
  background: #f2f2f2;
  color: #555;
  border-radius: 50%;
  width: 30px;
  height: 30px;
}
.btn-circle:hover {
  background: #e0e0e0;
}
.room-type-box {
  margin-bottom: 20px; /* Increase spacing */
}

/* Avatar Upload */
.avatar-upload {
  position: relative;
  max-width: 200px;
  margin: 10px auto;
}
.avatar-upload .avatar-edit {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 2;
}
.avatar-upload .avatar-edit input {
  display: none;
}
.avatar-upload .avatar-edit input + label {
  display: inline-block;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid transparent;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  position: relative;
}
.avatar-upload .avatar-edit input + label:after {
  content: '\f040';
  font-family: 'FontAwesome';
  color: #757575;
  position: absolute;
  top: 6px;
  left: 0;
  right: 0;
  text-align: center;
}
.avatar-upload .avatar-preview {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin: 10px auto;
}

/* Form Inputs */
.rb-shell .form-control {
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 4px 8px;
  font-size: 0.75rem;
  height: 30px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.rb-shell .form-control:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-20);
}

.rb-shell >>> .multiselect__tags {
  min-height: 30px !important;
  padding-top: 2px !important;
  border-radius: 6px !important;
  border-color: #cbd5e1 !important;
  font-size: 0.75rem !important;
}

.rb-shell >>> .multiselect__single,
.rb-shell >>> .multiselect__placeholder {
  font-size: 0.75rem !important;
  line-height: 22px !important;
}

.rb-shell >>> .multiselect__select {
  height: 28px !important;
}

.rb-shell >>> .multiselect__option {
  font-size: 0.75rem !important;
  padding: 6px 8px !important;
}

/* Validation Styles */
.error-border {
  border: 1px solid #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.success-border {
  border: 1px solid #28a745 !important;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
}

.error-messages {
  margin-top: 5px;
}

.error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 3px;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 3px;
  display: block;
}

.rb-hint {
  color: #6b7280;
  font-size: 12px;
  margin-top: 3px;
  display: block;
}

.room-type-box--error >>> .multiselect__tags {
  border: 1px solid #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

/* Alert Styles */
.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
}

.alert ul {
  padding-left: 20px;
  margin-top: 8px;
}

.alert li {
  margin-bottom: 4px;
}

/* Paragraph Textarea */
.paragraph {
  height: 70px;
  border-radius: 8px;
  resize: none;
}

/* Multiselect */
.multiselect__option {
  font-size: 14px;
  padding: 8px 12px;
}
.multiselect__tags {
  min-height: 38px;
}
.multiselect__input {
  min-height: 38px;
}

/* Booking Summary Amount */
.amount-display h6 {
  font-weight: 600;
  color: var(--brand-primary);
}

/* Payment Section */
.paymentmsg h6 {
  font-weight: 500;
  color: #242c67;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.paymentmsg input[type='checkbox'] {
  margin-left: 10px;
  accent-color: var(--brand-primary);
}

/* Misc Cards/Containers */
.imgContainer {
  max-width: 960px;
  margin: 10px auto;
  padding: 20px;
}
.card-content {
  padding: 10px;
}

/* Close Button for Rooms */
.close,
.btn-circle.btn-xl {
  border-radius: 50%;
  cursor: pointer;
}
.close {
  position: absolute;
  right: 16px;
  top: 4px;
  width: 25px;
  height: 25px;
  opacity: 0.6;
}
.close:hover {
  opacity: 1;
}

/* Misc Styles */
.product-image {
  width: 100%;
}
.spec-1 {
  color: #938787;
  font-size: 15px;
}
.para {
  font-size: 16px;
}
.person {
  width: 120px;
  border: 1px solid #ddd;
  height: 40px;
  border-radius: 20px;
}
.imagepic {
  width: 100%;
  height: 250px;
  object-fit: cover;
}
.dot {
  height: 7px;
  width: 7px;
  margin: 3px 6px;
  background-color: var(--brand-primary);
  border-radius: 50%;
  display: inline-block;
}
.ratings i {
  font-size: 16px;
  color: red;
}
.strike-text {
  color: red;
  text-decoration: line-through;
}
.countdetails {
  margin-bottom: 10px;
}
.roomamount {
  margin-left: 10px;
  margin-bottom: 10px;
}

/* Personal details — compact layout */
.personal-card {
  padding: 14px 16px;
}

.personal-card__header {
  margin-bottom: 10px;
}

.personal-card__title {
  margin: 0 0 8px;
  text-align: center;
  font-size: 15px;
}

.personal-card__customer-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.personal-card__search {
  flex: 1 1 180px;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.personal-card__search-input {
  padding: 4px 8px !important;
  font-size: 0.75rem !important;
  height: 30px;
}

.personal-card__badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
}

.personal-card__badge--found {
  background: #e8f5e9;
  color: #2e7d32;
}

.personal-card__badge--new {
  background: #fff3e0;
  color: #e65100;
}

.personal-card__badge--saving {
  background: #e3f2fd;
  color: #1565c0;
}

.personal-card__suggestions {
  position: relative;
  z-index: 4;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  max-height: 140px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.personal-card__suggestion {
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
}

.personal-card__suggestion:hover {
  background: #f8fafc;
}

.guest-fields-grid {
  margin: 0 -4px;
}

.form-group--compact {
  position: relative;
  margin-bottom: 0;
}

.form-group--compact .form-control {
  padding: 4px 24px 4px 8px;
  font-size: 0.75rem;
  height: 30px;
  text-align: left;
}

.rb-field--datepicker {
  min-height: 30px;
}

.rb-shell .kot-date-picker ::v-deep .vd-picker__input input {
  height: 30px !important;
  min-height: 30px !important;
  font-size: 0.75rem !important;
  padding: 0 8px 0 30px !important;
}

.room-booking-page--form-browser .form-group--compact i {
  top: 6px;
  font-size: 13px;
}

.room-booking-page--form-pos .form-group--compact i {
  top: 10px;
  font-size: 18px;
}

.room-booking-page--form-browser .personal-card {
  padding: 12px 14px;
}

.room-booking-page--form-browser .avatar-upload .avatar-preview {
  width: 72px;
  height: 72px;
}

.room-booking-page--form-browser .form-group--compact .form-control {
  height: 32px;
  font-size: 12px;
  padding: 5px 28px 5px 8px;
}

.room-booking-page--form-pos .personal-card {
  padding: 18px 20px;
}

.room-booking-page--form-pos .form-group--compact {
  margin-bottom: 12px;
}

.room-booking-page--form-pos .rb-shell .form-control {
  height: 40px;
  font-size: 0.9375rem;
}

.room-booking-page--form-pos .rb-btn {
  height: 42px;
  font-size: 0.9375rem;
}

.room-booking-page--form-pos .form-group--compact .form-control {
  height: 40px;
  font-size: 0.9375rem;
  padding: 8px 32px 8px 12px;
}

.room-booking-page--form-pos .personal-card__search-input {
  height: 44px !important;
  font-size: 16px !important;
}

.room-booking-page--form-pos .avatar-upload .avatar-preview {
  width: 100px;
  height: 100px;
}
</style>
